import React from 'react';
import Spinner from 'jsx/elements/spinner';
import { usePortfolioTranslation as useLocale } from 'jsx/hooks/useTranslation';
import Flex from 'jsx/layout/Flex';
import Button from 'jsx/elements/buttons/Button';
import CustomDropDown from 'jsx/elements/buttons/CustomDropDown';

import { api } from 'api';
import { useMonitoring } from 'components/MonitorProvider';
import MonitoringWatchlistModal from 'components/MonitoringWatchlistModal';
import PortfolioFileUpdateModal from 'components/PortfolioFileUpdateModal';
import PortfolioRegnoUpdateModal from 'components/PortfolioRegnoUpdateModal';

import Entry from './DashboardOverviewEntry';
import prepareDashboardData from './prepareDashboardData';
import './DashboardOverview.less';

export default function DashboardOverview() {
  const [modal, setModal] = React.useState('');
  const { selected } = useMonitoring();
  const [{ status, data }, setStatisticsData] = React.useState({ status: 'loading', data: null });

  React.useEffect(() => {
    api.monitor.loadStatistics(selected.monitorId).then((res) => {
      setStatisticsData({ status: 'loaded', data: res });
    });
  }, [selected.monitorId]);
  const { t } = useLocale();

  const { individual, company, portfolio } = prepareDashboardData(data);

  function launch(modal) {
    setModal(modal);
  }

  function close() {
    setModal('');
  }

  const updateItems = [
    {
      name: t('portfolioMonitoring_updateItems_uploadRegnoList'),
      onClick: () => launch('update-regno'),
      key: 0,
    },
    {
      name: t('portfolioMonitoring_updateItems_uploadFile'),
      onClick: () => launch('update-file'),
      key: 1,
    },
  ];

  return (
    <>
      <div className="dashboard-section-header gutter-top">
        <h2>{t('dashboard_header_overview')}</h2>
        <Flex>
          <Button
            color="gray-light"
            text={t('monitoring_watchlist_button')}
            onClick={() => launch('watchlist')}
          />
          {selected.accessLevel > 1 && (
            <div style={{ marginLeft: 10 }}>
              <CustomDropDown
                title={t('portfolioMonitoring_updateDropdown_title')}
                items={updateItems}
              />
            </div>
          )}
        </Flex>
      </div>
      {modal === 'update-regno' && (
        <PortfolioRegnoUpdateModal monitorId={selected.monitorId} closeModal={close} />
      )}
      {modal === 'update-file' && (
        <PortfolioFileUpdateModal monitorId={selected.monitorId} closeModal={close} />
      )}
      {modal === 'watchlist' && (
        <MonitoringWatchlistModal
          selected={selected}
          closeModal={close}
          canRemove={selected.accessLevel > 1}
        />
      )}
      <div className="border-primary collection-status-table" style={{ overflowX: 'auto' }}>
        {status !== 'loaded' && <Spinner relCenter />}
        <table
          className="text-gray-darker text-14 text-center text-semibold"
          style={{ opacity: status === 'loaded' ? 1 : 0.3, minWidth: 950 }}
        >
          <colgroup>
            <col width="11.11%" />
            <col width="11.11%" />
            <col width="11.11%" />
            <col width="11.11%" />
            <col width="11.11%" />
            <col width="11.11%" />
            <col width="11.11%" />
            <col width="11.11%" />
            <col width="11.11%" />
          </colgroup>
          <thead>
            <tr className="border-top border-bottom" style={{ lineHeight: '1.3em', height: 60 }}>
              <th style={{ padding: 5 }} />
              <th style={{ padding: 5 }}>{t('dashboard_overview_h_0')}</th>
              <th style={{ padding: 5 }}>{t('dashboard_overview_h_1')}</th>
              <th style={{ padding: 5 }}>{t('dashboard_overview_h_2')}</th>
              <th style={{ padding: 5 }}>{t('dashboard_overview_h_3')}</th>
              <th style={{ padding: 5 }}>{t('dashboard_overview_h_4')}</th>
              <th style={{ padding: 5 }}>{t('dashboard_overview_h_5')}</th>
              <th style={{ padding: 5 }}>{t('dashboard_overview_h_6')}</th>
              <th style={{ padding: 5 }}>{t('dashboard_overview_h_7')}</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-bottom" style={{ height: 60 }}>
              <td>{t('dashboard_overview_h_companies')}</td>
              <Entry value={company.a} query="company=true&veryLowRisk=true" variant="ltgreen" />
              <Entry value={company.b} query="company=true&lowRisk=true" variant="ltgreen" />
              <Entry value={company.c} query="company=true&possibleRisk=true" variant="yellow" />
              <Entry value={company.d} query="company=true&highRisk=true" variant="pink" />
              <Entry
                value={company.e}
                color="#C40F11"
                query="company=true&sevGroup1=true&sevGroup2=true&sevGroup3=true"
                variant="red"
              />
              <Entry value={company.f} query="company=true&deceased=true" variant="gray" />
              <Entry
                value={company.g}
                query="company=true&notOnDDRegistry=true&cipNotCalculated=true"
                variant="gray"
              />
              <Entry value={company.sum} query="company=true" />
            </tr>
            <tr className="border-bottom" style={{ height: 60 }}>
              <td>{t('dashboard_overview_h_individuals')}</td>
              <Entry
                value={individual.a}
                query="individual=true&veryLowRisk=true"
                variant="ltgreen"
              />
              <Entry value={individual.b} query="individual=true&lowRisk=true" variant="ltgreen" />
              <Entry
                value={individual.c}
                query="individual=true&possibleRisk=true"
                variant="yellow"
              />
              <Entry value={individual.d} query="individual=true&highRisk=true" variant="pink" />
              <Entry
                value={individual.e}
                query="individual=true&sevGroup1=true&sevGroup2=true&sevGroup3=true"
                variant="red"
              />
              <Entry value={individual.f} query="individual=true&deceased=true" variant="gray" />
              <Entry
                value={individual.g}
                query="individual=true&notOnDDRegistry=true&cipNotCalculated=true"
                variant="gray"
              />
              <Entry value={individual.sum} query="individual=true" />
            </tr>
            <tr style={{ height: 60 }}>
              <td>{t('dashboard_overview_h_total')}</td>
              <Entry value={portfolio.a} query="veryLowRisk=true" />
              <Entry value={portfolio.b} query="lowRisk=true" />
              <Entry value={portfolio.c} query="possibleRisk=true" />
              <Entry value={portfolio.d} query="highRisk=true" />
              <Entry
                value={portfolio.e}
                query="sevGroup1=true&sevGroup2=true&sevGroup3=true&sevGroupOther=true"
              />
              <Entry value={portfolio.f} query="deceased=true" />
              <Entry value={portfolio.g} query="notOnDDRegistry=true&cipNotCalculated=true" />
              <Entry value={portfolio.sum} />
            </tr>
          </tbody>
        </table>
      </div>
    </>
  );
}
